import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Images, X } from 'lucide-react';
import { createPortal } from 'react-dom';
import { xamplinApi, PURPOSE_BY_ID, entityIdOf, deriveEventStatus, type XpPhoto } from '@/lib/api/xamplin';
import { queryKeys } from '@/lib/api/queryKeys';
import { cn } from '@/lib/cn';
import { IntakeInviteButton } from '@/features/intake/IntakeInviteButton';

/**
 * Per-event Measure dashboard (build-v2 G3) — 4 tabs faithful to the prototype
 * `event-dashboard.jsx`: Overview (MENT ring + KPIs), Consumer (CS-1 funnel +
 * demographics), Asset-by-Purpose (photos grouped SEE/ENGAGE/TRY/ACT) and Team
 * feedback (stakeholder sign-off) + a highlight reel modal. All real data.
 */
type Tab = 'overview' | 'consumer' | 'assets' | 'team';
const PURPOSE_COLOR: Record<string, string> = {
  SEE: '#5aa9ff', ENGAGE: '#f4b740', TRY: '#4dd28b', ACT: '#ff5a4f',
};

export function EventMeasurePage() {
  const { eventId, projectId } = useParams<{ projectId: string; eventId: string }>();
  const id = eventId ? Number(eventId) : 0;
  const [tab, setTab] = useState<Tab>('overview');
  const [reelOpen, setReelOpen] = useState(false);

  const measureQ = useQuery({
    queryKey: queryKeys.xamplin.eventMeasure(id),
    queryFn: () => xamplinApi.eventMeasure(id),
    enabled: id > 0,
  });
  const photosQ = useQuery({
    queryKey: queryKeys.xamplin.photoFeed({ eventId: id, scope: 'event' }),
    queryFn: () => xamplinApi.photos.feed({ eventId: id, limit: 80 }),
    enabled: id > 0,
  });
  const pid = projectId ? Number(projectId) : 0;
  const projectsQ = useQuery({ queryKey: queryKeys.xamplin.projects(), queryFn: () => xamplinApi.projects.list() });
  const structureQ = useQuery({
    queryKey: queryKeys.xamplin.projectStructure(pid),
    queryFn: () => xamplinApi.projects.structure(pid),
    enabled: pid > 0,
  });
  const m = measureQ.data;
  const photos = photosQ.data?.items ?? [];

  // Overview meta (G3 parity): objective/audience/region/type/stage from the
  // project + the event's group, mirroring the prototype OverviewTab.
  const project = projectsQ.data?.items.find((p) => p.id === pid);
  const meta = (() => {
    const groups = structureQ.data?.structured?.project?.eventGroups ?? [];
    let region = '—';
    let stage = '—';
    for (const g of groups)
      for (const e of g.events ?? []) {
        if (entityIdOf(e.id) === id) {
          region = e.location || g.name || '—';
          stage = deriveEventStatus(e);
        }
      }
    return {
      primary: project?.marketingObjectivePrimary || project?.objectiveType || '—',
      secondary: project?.marketingObjectiveSecondary || '—',
      audience: project?.objectiveType || '—',
      region,
      eventType: project?.objectiveType || 'Sampling',
      stage,
    };
  })();

  return (
    <div className="page flex flex-col gap-5">
      <div className="flex items-center gap-3">
        <Link
          to={projectId ? `/projects/${projectId}` : '/projects'}
          className="grid h-8 w-8 place-items-center rounded-lg border border-[var(--color-xk-border)] text-[var(--color-xk-text-muted)] hover:text-[var(--color-xk-lavender)]"
        >
          <ArrowLeft className="h-4 w-4" />
        </Link>
        <div>
          <h1 className="font-display text-2xl font-extrabold tracking-tight text-[var(--color-xk-lavender)]">
            {m?.name ?? 'Event measure'}
          </h1>
          <p className="mono text-xs uppercase tracking-wider text-[var(--color-xk-text-muted)]">
            Measure · event dashboard
          </p>
        </div>
        {id > 0 && (
          <div className="ml-auto flex items-center gap-2">
            {photos.length > 0 && (
              <button
                type="button"
                onClick={() => setReelOpen(true)}
                className="mono flex items-center gap-1.5 rounded-lg border border-[var(--color-xk-border)] px-3 py-1.5 text-[11px] font-bold text-[var(--color-xk-text-muted)] hover:border-[var(--color-xk-accent)] hover:text-[var(--color-xk-lavender)]"
              >
                <Images className="h-3.5 w-3.5" /> Highlight reel
              </button>
            )}
            <IntakeInviteButton scope="event" targetId={id} role="venue_stakeholder" label="Invitar al lugar" />
          </div>
        )}
      </div>

      {measureQ.isLoading && <div className="mono text-sm text-[var(--color-xk-text-muted)]">Cargando…</div>}
      {measureQ.isError && (
        <div className="rounded-xl border border-[var(--color-error)] bg-[var(--color-error)]/10 px-4 py-3 text-sm text-[var(--color-error)]">
          No se pudo cargar la medición del evento.
        </div>
      )}

      {m && (
        <>
          {/* Tabs */}
          <div className="flex gap-1 border-b border-[var(--color-xk-border)]">
            {([
              ['overview', 'Overview'],
              ['consumer', 'Consumer'],
              ['assets', 'Asset by Purpose'],
              ['team', 'Team feedback'],
            ] as [Tab, string][]).map(([t, label]) => (
              <button
                key={t}
                type="button"
                onClick={() => setTab(t)}
                className={cn(
                  'border-b-2 px-4 py-2 text-sm font-semibold transition-colors',
                  tab === t
                    ? 'border-[var(--color-xk-accent)] text-[var(--color-xk-accent)]'
                    : 'border-transparent text-[var(--color-xk-text-muted)] hover:text-[var(--color-xk-lavender)]',
                )}
              >
                {label}
              </button>
            ))}
          </div>

          {tab === 'overview' && (
            <div className="flex flex-col gap-6">
            <Panel title="Event brief">
              <div className="grid grid-cols-2 gap-x-6 gap-y-2 sm:grid-cols-3">
                {([
                  ['Primary objective', meta.primary],
                  ['Secondary objective', meta.secondary],
                  ['Target audience', meta.audience],
                  ['Region / market', meta.region],
                  ['Event type', meta.eventType],
                  ['Activation stage', meta.stage],
                ] as const).map(([label, value]) => (
                  <div key={label}>
                    <div className="mono text-[10px] uppercase tracking-wider text-[var(--color-xk-text-subtle)]">{label}</div>
                    <div className="truncate text-sm capitalize text-[var(--color-xk-lavender)]">{value}</div>
                  </div>
                ))}
              </div>
            </Panel>
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-[240px_1fr]">
              <Panel title="MENT score">
                <div className="flex flex-col items-center gap-2 py-2">
                  <RingProgress value={m.ment} max={10} color="var(--color-xk-accent)" />
                  <span className="mono text-[11px] text-[var(--color-xk-text-muted)]">/ 10 composite</span>
                </div>
              </Panel>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                <Kpi label="Attendance" value={m.attendance.toLocaleString()} tone="#4dd28b" />
                <Kpi label="CS-1 responses" value={m.responses.toLocaleString()} />
                <Kpi label="Avg rating · /5" value={m.avgRating ? String(m.avgRating) : '—'} tone="#5aa9ff" />
                <Kpi
                  label="Response rate"
                  value={m.funnel.scanned ? `${Math.round((m.funnel.started / m.funnel.scanned) * 100)}%` : '—'}
                />
                <Kpi label="Opt-in" value={m.optIn.toLocaleString()} />
                <Kpi
                  label="Stakeholder"
                  value={m.stakeholder ? `${m.stakeholder.alignedPct}%` : '—'}
                  tone="#4dd28b"
                />
              </div>
            </div>
            </div>
          )}

          {tab === 'consumer' && (
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              <Panel title="Trial-validation funnel">
                <Funnel funnel={m.funnel} />
              </Panel>
              <Panel title="Demographics">
                <Demographics gender={m.demographics.gender} />
              </Panel>
            </div>
          )}

          {tab === 'assets' && <AssetsByPurpose photos={photos} loading={photosQ.isLoading} />}

          {tab === 'team' && (
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              <Panel title="Stakeholder sign-off">
                {m.stakeholder && m.stakeholder.total > 0 ? (
                  <div className="flex flex-col gap-3">
                    <div className="flex gap-6">
                      <div>
                        <div className="font-display text-3xl font-extrabold text-[#4dd28b]">
                          {m.stakeholder.alignedPct}%
                        </div>
                        <div className="mono text-[10px] uppercase tracking-wider text-[var(--color-xk-text-subtle)]">
                          coincidió con el brief
                        </div>
                      </div>
                      <div>
                        <div className="font-display text-3xl font-extrabold text-[var(--color-xk-lavender)]">
                          {m.stakeholder.avgRating}
                        </div>
                        <div className="mono text-[10px] uppercase tracking-wider text-[var(--color-xk-text-subtle)]">
                          rating · /5
                        </div>
                      </div>
                    </div>
                    <div className="h-3 overflow-hidden rounded-full bg-[var(--color-xk-bg)]">
                      <div className="h-full rounded-full bg-[#4dd28b]" style={{ width: `${m.stakeholder.alignedPct}%` }} />
                    </div>
                  </div>
                ) : (
                  <p className="mono text-xs text-[var(--color-xk-text-muted)]">Sin sign-off del stakeholder aún.</p>
                )}
              </Panel>
              <Panel title="Field photos">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-[var(--color-xk-lavender)]">{photos.length} fotos de campo</span>
                  {photos.length > 0 && (
                    <button
                      type="button"
                      onClick={() => setReelOpen(true)}
                      className="mono text-[11px] text-[var(--color-xk-accent)] hover:underline"
                    >
                      Ver highlight reel →
                    </button>
                  )}
                </div>
              </Panel>
            </div>
          )}
        </>
      )}

      {reelOpen && <HighlightReel photos={photos} onClose={() => setReelOpen(false)} />}
    </div>
  );
}

/** SVG ring for the MENT score (build-v2 G3). */
function RingProgress({ value, max, color }: { value: number; max: number; color: string }) {
  const r = 46;
  const c = 2 * Math.PI * r;
  const pct = Math.max(0, Math.min(1, value / max));
  return (
    <svg viewBox="0 0 120 120" className="h-32 w-32">
      <circle cx="60" cy="60" r={r} fill="none" stroke="var(--color-xk-bg)" strokeWidth="10" />
      <circle
        cx="60"
        cy="60"
        r={r}
        fill="none"
        stroke={color}
        strokeWidth="10"
        strokeLinecap="round"
        strokeDasharray={c}
        strokeDashoffset={c * (1 - pct)}
        transform="rotate(-90 60 60)"
      />
      <text x="60" y="58" textAnchor="middle" className="font-display" fontSize="28" fontWeight="800" fill="var(--color-xk-lavender)">
        {value || '—'}
      </text>
      <text x="60" y="78" textAnchor="middle" fontSize="10" fill="var(--color-xk-text-subtle)">
        MENT
      </text>
    </svg>
  );
}

interface FunnelData { scanned: number; started: number; tried: number; enjoyed: number; shifted: number }
function Funnel({ funnel }: { funnel: FunnelData }) {
  const rows: [string, number][] = [
    ['QR scanned', funnel.scanned],
    ['Started', funnel.started],
    ['Tried', funnel.tried],
    ['Enjoyed', funnel.enjoyed],
    ['Pref. shift', funnel.shifted],
  ];
  return (
    <div className="flex flex-col gap-1.5">
      {rows.map(([label, n]) => {
        const pct = funnel.scanned ? Math.round((n / funnel.scanned) * 100) : 0;
        return (
          <div key={label} className="flex items-center gap-3">
            <span className="w-24 text-[11px] text-[var(--color-xk-lavender)]">{label}</span>
            <div className="h-3 flex-1 overflow-hidden rounded-full bg-[var(--color-xk-bg)]">
              <div className="h-full rounded-full bg-[var(--color-xk-accent)]" style={{ width: `${pct}%` }} />
            </div>
            <span className="mono w-16 text-right text-[11px] text-[var(--color-xk-text-muted)]">
              {n.toLocaleString()} · {pct}%
            </span>
          </div>
        );
      })}
    </div>
  );
}

function Demographics({ gender }: { gender: { label: string; n: number }[] }) {
  const total = gender.reduce((s, x) => s + x.n, 0) || 1;
  return (
    <div className="flex flex-col gap-2.5">
      {gender.map((g) => {
        const pct = Math.round((g.n / total) * 100);
        const color = g.label === 'Mujer' ? '#B262F5' : g.label === 'Hombre' ? '#5aa9ff' : '#f4b740';
        return (
          <div key={g.label} className="flex items-center gap-3">
            <span className="w-16 text-[11px] text-[var(--color-xk-lavender)]">{g.label}</span>
            <div className="h-3 flex-1 overflow-hidden rounded-full bg-[var(--color-xk-bg)]">
              <div className="h-full rounded-full" style={{ width: `${pct}%`, background: color }} />
            </div>
            <span className="mono w-12 text-right text-[11px] text-[var(--color-xk-text-muted)]">{pct}%</span>
          </div>
        );
      })}
    </div>
  );
}

function AssetsByPurpose({ photos, loading }: { photos: XpPhoto[]; loading: boolean }) {
  if (loading) return <div className="mono text-sm text-[var(--color-xk-text-muted)]">Cargando assets…</div>;
  const purposes = ['SEE', 'ENGAGE', 'TRY', 'ACT'] as const;
  return (
    <div className="flex flex-col gap-5">
      {purposes.map((p) => {
        const items = photos.filter((ph) => ph.purposeId && PURPOSE_BY_ID[ph.purposeId] === p);
        return (
          <div key={p} className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <span className="mono rounded-full px-2 py-0.5 text-[10px] font-bold text-white" style={{ background: PURPOSE_COLOR[p] }}>
                {p}
              </span>
              <span className="mono text-[11px] text-[var(--color-xk-text-muted)]">{items.length}</span>
            </div>
            {items.length === 0 ? (
              <span className="mono text-[11px] text-[var(--color-xk-text-subtle)]">— sin assets —</span>
            ) : (
              <div className="scrollbar-none flex gap-2 overflow-x-auto pb-1">
                {items.slice(0, 16).map((ph) => (
                  <img
                    key={ph.id}
                    src={ph.image}
                    alt=""
                    loading="lazy"
                    className="h-20 w-20 flex-none rounded-lg border border-[var(--color-xk-border)] object-cover"
                  />
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function HighlightReel({ photos, onClose }: { photos: XpPhoto[]; onClose: () => void }) {
  return createPortal(
    <div className="fixed inset-0 z-[100] flex flex-col bg-black/85 backdrop-blur-sm" onClick={onClose}>
      <div className="flex items-center gap-3 px-5 py-3">
        <span className="font-display text-sm font-extrabold text-white">Highlight reel · {photos.length}</span>
        <button type="button" onClick={onClose} className="ml-auto grid h-8 w-8 place-items-center rounded-lg text-white/70 hover:text-white">
          <X className="h-5 w-5" />
        </button>
      </div>
      <div className="grid flex-1 grid-cols-2 gap-3 overflow-auto p-5 sm:grid-cols-3 lg:grid-cols-5" onClick={(e) => e.stopPropagation()}>
        {photos.map((p) => (
          <img key={p.id} src={p.image} alt="" loading="lazy" className="aspect-square w-full rounded-xl object-cover" />
        ))}
      </div>
    </div>,
    document.body,
  );
}

function Kpi({ label, value, tone }: { label: string; value: string; tone?: string }) {
  return (
    <div className="rounded-xl border border-[var(--color-xk-border)] bg-[var(--color-xk-surface)] px-3 py-2.5">
      <div className="font-display text-2xl font-extrabold" style={{ color: tone ?? 'var(--color-xk-lavender)' }}>
        {value}
      </div>
      <div className="mono text-[10px] uppercase tracking-wider text-[var(--color-xk-text-subtle)]">{label}</div>
    </div>
  );
}

function Panel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-3 rounded-2xl border border-[var(--color-xk-border)] bg-[var(--color-xk-surface)] p-4">
      <h2 className="mono text-xs font-bold uppercase tracking-wider text-[var(--color-xk-text-muted)]">{title}</h2>
      {children}
    </div>
  );
}
